import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { toast } from 'react-toastify';
import { Dispatch, connect } from "react-redux";
import { IApplicationState, PhlebotActionCreators, AppActionCreators } from '@nkr/store/index';
import { ICenter, IPhlebotRequest, IListItem, IBaseResponse } from '@nkr/models/index';
import { IPhlebotRequestResponse, IPhlebotLookupResponse } from '@nkr/store/phlebot-service';
import { GridApi, ColumnApi, GridReadyEvent } from 'ag-grid-community';
import PhlebotTracker from './phlebot-tracker';

interface IProps {
    centerId: number
}

interface IStateProps {
    center: ICenter
}

interface IDispatchProps {
    hideLoading: () => void,
    showLoading: (message: string) => Promise<void>,
    getRequestList: (centerId: number, startDate: Date, endDate: Date, dateType: number) => Promise<IPhlebotRequestResponse>,
    getPhlebotLookup: () => Promise<IPhlebotLookupResponse>,
    reorderRequest: (requestId: number) => Promise<IBaseResponse>,
    updateRequest: (request: IPhlebotRequest) => Promise<IBaseResponse>,
    updateStatusToSent: (requestId: number) => Promise<IBaseResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    requests: IPhlebotRequest[],
    kitTypes: IListItem[],
    stateList: IListItem[],
    gridApi: GridApi,
    columnApi: ColumnApi
}

class PhlebotReport extends React.Component<TProps, IState> {
    readonly state: IState = {
        requests: [],
        kitTypes: [],
        stateList: [],
        gridApi: null,
        columnApi: null
    }

    handleTabSelect = (key: string) => { }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.getRequestsByDateRange(moment().subtract(6, 'month').toDate(), moment().toDate(), 0);
    }

    getRequestsByDateRange = (startDate: Date, endDate: Date, dateType: number) => {
        let start = this.props.showLoading('Getting phlebot requests, one moment please...');

        start.then(() => {
            Promise.all([
                this.props.getRequestList(this.props.centerId, startDate, endDate, dateType),
                this.props.getPhlebotLookup()
            ])
                .then(([listResponse, lookupReponse]) => {

                    if (listResponse.data.success) {
                        let phlebotRequests = listResponse.data.results;
                        const { phlebotKitTypes, stateList } = lookupReponse.data.results;

                        this.setState({
                            requests: phlebotRequests,
                            kitTypes: phlebotKitTypes,
                            stateList
                        });
                    };
                })
                .catch(function(err) {
                    toast.error("Unable to complete search.  Please ensure your search parameters are populated.", { autoClose: 7000 });
                })
                .finally(() => {
                    this.props.hideLoading();
                  });
        });
    }

    onGridReady = (e: GridReadyEvent) => {
        // this.gridApi = params.api;
        // this.columnApi = params.columnApi;
        this.setState({
            gridApi: e.api,
            columnApi: e.columnApi
        });
    }

    updateSearch = (startDate: string, endDate: string, dateType: number) => {
        this.getRequestsByDateRange(moment(startDate).toDate(), moment(endDate).toDate(), dateType);
    }

    updateRequestToSentStatus = (requestId: number) => {
        return this.props.updateStatusToSent(requestId);
    }

    updateRequest = (request: IPhlebotRequest) => {
        this.props.updateRequest(request).then((response) => {
            if (response.data.success) {

                //update table
                let updatedList = _.cloneDeep(this.state.requests).map(item => {
                    if (item.id == request.id) {
                        item = request;
                    }

                    return item;
                });

                this.setState({
                    requests: updatedList
                });
                toast.success("Request for " + request.firstName + " " + request.lastName + " has been updated.");
            } else {
                toast.error("Unable to update request.", { autoClose: 7000 });
            }
        });
    }

    updateRequestInViewToSent = (requestId: number) => {
        let list = _.cloneDeep(this.state.requests).map(item => {
            if (item.id == requestId) {
                item.status = 0; //updates to sent
            }

            return item;
        });

        this.setState({
            requests: list
        });
    }

    reorderRequest = (requestId: number) => {
        this.props.reorderRequest(requestId)
            .then((response) => {

                if (response.data.success) {
                    this.updateRequestInViewToSent(requestId);

                    toast.success('Reorder for ID ' + requestId + ' complete');
                } else {
                    toast.error(response.data.message, { autoClose: 7000 });
                }
            });
    }

    render = () => {
        let isAdminView = this.props.centerId == 0;

        return (
            <div style={{ padding: '0px 10px' }}>
                <PhlebotTracker
                    isAdminView={isAdminView}
                    list={this.state.requests}
                    types={this.state.kitTypes}
                    states={this.state.stateList}
                    onGridReady={this.onGridReady}
                    onSearchUpdate={this.updateSearch}
                    updateRequestToSentStatus={this.updateRequestToSentStatus}
                    updateRequestInViewToSent={this.updateRequestInViewToSent}
                    reorderRequest={this.reorderRequest}
                    updateRequest={this.updateRequest}
                />
            </div>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        center: appState.app.center
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getRequestList: (centerId, startDate, endDate, dateType) => {
            return dispatch(PhlebotActionCreators.getRequestListRange(centerId, startDate, endDate, dateType));
        },
        getPhlebotLookup: () => {
            return dispatch(PhlebotActionCreators.getLookupData());
        },
        reorderRequest: (requestId) => {
            return dispatch(PhlebotActionCreators.reorderRequest(requestId));
        },
        updateRequest: (request) => {
            return dispatch(PhlebotActionCreators.updateRequest(request));
        },
        updateStatusToSent: (requestId) => {
            return dispatch(PhlebotActionCreators.updateStatusToSent(requestId));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(PhlebotReport);